import React, { useCallback, useEffect, useState } from "react";
import Layout from "../Layout/Layout";
import styled from "styled-components";
import StoryImg from "../Images/Create-story.jpg";
import PoetryImg from "../Images/Create-poetry.jpg";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setPageTitle } from "../utils/titleUtils";
import { Folder, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import {
  getStoryDrafts,
  getPoemDrafts,
  deleteStoryDraft,
  deletePoemDraft,
} from "./utilities/ApiCalls";

const isNonContestDraft = (draft) => {
  const contestId = draft?.contestId;
  if (!contestId) return true;
  if (typeof contestId === "string") {
    return contestId === "" || contestId === "0" || contestId === "null";
  }
  return false;
};

const formatUpdatedAt = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
};

const Create = () => {
  const [t] = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const [drafts, setDrafts] = useState([]);
  const [draftCount, setDraftCount] = useState(0);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [deletingDraftId, setDeletingDraftId] = useState(null);

  const fetchDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const [storyResp, poemResp] = await Promise.all([
        getStoryDrafts({}),
        getPoemDrafts({}),
      ]);

      const storyDrafts =
        storyResp.status === "Success"
          ? (storyResp.data || []).filter(isNonContestDraft).map((d) => ({
              ...d,
              contentType: "story",
            }))
          : [];

      const poemDrafts =
        poemResp.status === "Success"
          ? (poemResp.data || []).filter(isNonContestDraft).map((d) => ({
              ...d,
              contentType: "poem",
            }))
          : [];

      const merged = [...storyDrafts, ...poemDrafts].sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setDrafts(merged);
      setDraftCount(merged.length);
    } catch {
      setDrafts([]);
      setDraftCount(0);
    } finally {
      setLoadingDrafts(false);
    }
  }, []);

  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handleMyDraftsClick = async () => {
    if (draftCount === 0) {
      toast.info("No drafts present. Create a new story or poem draft.");
      return;
    }
    await fetchDrafts();
    setIsDraftsModalOpen(true);
  };

  const openDraft = (draft) => {
    setIsDraftsModalOpen(false);
    const draftQuery = `id=${draft._id}&isDraft=1&step=content`;
    if (draft.contentType === "story") {
      history.push(`/story-create?${draftQuery}`);
    } else {
      history.push(`/poem-create?${draftQuery}`);
    }
  };

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;

    const { _id, contentType, name } = draftToDelete;
    setDeletingDraftId(_id);

    try {
      const resp =
        contentType === "story"
          ? await deleteStoryDraft(_id)
          : await deletePoemDraft(_id);

      if (resp.status === "Success") {
        toast.success(`Draft "${name || "Untitled"}" deleted.`);
        setDraftToDelete(null);
        const nextCount = draftCount - 1;
        setDrafts((prev) => prev.filter((d) => d._id !== _id));
        setDraftCount(Math.max(0, nextCount));
        if (nextCount === 0) {
          setIsDraftsModalOpen(false);
        }
      } else {
        toast.error(resp.message || "Unable to delete draft.");
      }
    } catch {
      toast.error("Unable to delete draft.");
    } finally {
      setDeletingDraftId(null);
    }
  };

  return (
    <Layout>
      <Toaster />
      <Root>
        <div className="create-header-section">
          <h1 className="create-main-heading">Start Writing</h1>
          <p className="create-sub-heading">
            Choose a format to begin your journey.
          </p>
        </div>

        <div className="create-cards-wrapper">
          <div
            className="create-card"
            onClick={() => history.push("/story-create")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                history.push("/story-create");
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="create-image-container">
              <img alt="Story" className="img" src={StoryImg} />
            </div>
            <div className="create-card-content">
              <h2 className="create-card-title">Story</h2>
              <p className="create-card-desc">
                Craft characters, build worlds, and weave narratives. Perfect
                for short fiction, memoirs, and novels.
              </p>
              <button type="button" className="create-action-btn">
                CREATE STORY
              </button>
            </div>
          </div>

          <div
            className="create-card"
            onClick={() => history.push("/poem-create")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                history.push("/poem-create");
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="create-image-container">
              <img alt="Poetry" className="img" src={PoetryImg} />
            </div>
            <div className="create-card-content">
              <h2 className="create-card-title">Poetry</h2>
              <p className="create-card-desc">
                Express emotion through rhythm and imagery. Ideal for haikus,
                free verse, and sonnets.
              </p>
              <button type="button" className="create-action-btn">
                CREATE POETRY
              </button>
            </div>
          </div>
        </div>

        <div className="create-drafts-section">
          <div className="create-drafts-divider" />
          <button
            type="button"
            className={`create-drafts-btn${draftCount === 0 ? " disabled" : ""}`}
            onClick={handleMyDraftsClick}
            aria-disabled={draftCount === 0}
          >
            <Folder size={16} className="create-drafts-icon" />
            <span>My Drafts</span>
            {draftCount > 0 && (
              <span className="create-drafts-badge">{draftCount}</span>
            )}
          </button>
        </div>

        <div className="create-footer-quote">"{t("Create.Dailouge")}"</div>

        {isDraftsModalOpen && (
          <div
            className="create-modal-backdrop"
            onClick={() => setIsDraftsModalOpen(false)}
          >
            <div
              className="create-modal create-drafts-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="create-modal-close"
                onClick={() => setIsDraftsModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>

              <h2 className="create-modal-title">My Drafts</h2>

              {loadingDrafts ? (
                <p className="create-modal-text">Loading drafts...</p>
              ) : drafts.length === 0 ? (
                <p className="create-modal-text">
                  No drafts found. Create a new story or poem to get started.
                </p>
              ) : (
                <ul className="create-drafts-list">
                  {drafts.map((draft) => (
                    <li key={`${draft.contentType}-${draft._id}`}>
                      <button
                        type="button"
                        className="create-draft-row"
                        onClick={() => openDraft(draft)}
                      >
                        <div className="create-draft-row-main">
                          <span className="create-draft-title">
                            {draft.name || "(Untitled)"}
                          </span>
                          <span
                            className={`create-draft-type create-draft-type--${draft.contentType}`}
                          >
                            {draft.contentType === "story" ? "Story" : "Poetry"}
                          </span>
                        </div>
                        {draft.updatedAt && (
                          <span className="create-draft-updated">
                            Updated {formatUpdatedAt(draft.updatedAt)}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        className="create-draft-delete"
                        aria-label={`Delete draft ${draft.name || "Untitled"}`}
                        disabled={deletingDraftId === draft._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDraftToDelete(draft);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {draftToDelete && (
          <div className="create-modal-backdrop create-confirm-backdrop">
            <div className="create-modal create-confirm-modal">
              <h2 className="create-modal-title">Delete draft?</h2>
              <p className="create-modal-text">
                Delete &ldquo;{draftToDelete.name || "Untitled"}&rdquo;? This
                cannot be undone.
              </p>
              <div className="create-modal-actions">
                <button
                  type="button"
                  className="create-modal-btn secondary"
                  onClick={() => setDraftToDelete(null)}
                  disabled={!!deletingDraftId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="create-modal-btn danger"
                  onClick={confirmDeleteDraft}
                  disabled={!!deletingDraftId}
                >
                  {deletingDraftId ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </Root>
    </Layout>
  );
};

export default Create;

const Root = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  font-family: "Inter", sans-serif;

  .create-header-section {
    text-align: center;
    margin-bottom: 40px;
  }

  .create-main-heading {
    font-size: 42px;
    color: var(--color-primary);
    font-weight: 700;
    margin-bottom: 12px;
    font-family: "Libre Baskerville", serif;
  }

  .create-sub-heading {
    font-size: 16px;
    color: var(--color-text);
    font-family: "Inter", sans-serif;
    background-color: var(--color-background);
    display: inline-block;
    padding: 0 10px;
  }

  .create-cards-wrapper {
    display: flex;
    gap: 30px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 32px;
    width: 100%;
    max-width: 900px;
  }

  .create-card {
    flex: 1;
    min-width: 280px;
    max-width: 400px;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 8px;
    overflow: hidden;
    text-decoration: none;
    color: inherit;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    cursor: pointer;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }
  }

  .create-image-container {
    height: 200px;
    width: 100%;
    overflow: hidden;

    .img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      margin-top: 0px;
    }
  }

  .create-card-content {
    padding: 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
  }

  .create-card-title {
    font-size: 28px;
    color: var(--color-text-tertiary);
    margin-bottom: 12px;
    font-family: "Libre Baskerville", serif;
    font-weight: 700;
  }

  .create-card-desc {
    font-size: 15px;
    color: var(--color-text);
    margin-bottom: 24px;
    line-height: 1.5;
    font-family: "Inter", sans-serif;
    flex-grow: 1;
  }

  .create-action-btn {
    background: transparent;
    border: 1px solid var(--color-border);
    padding: 10px 28px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1px;
    cursor: pointer;
    text-transform: uppercase;
    transition: all 0.3s;
    color: var(--color-text-tertiary);
    margin-top: auto;
    font-family: "Inter", sans-serif;
    pointer-events: none;

    &:hover {
      background: var(--color-text-tertiary);
      color: var(--color-surface);
      border-color: var(--color-text-tertiary);
    }
  }

  .create-drafts-section {
    position: relative;
    width: 100%;
    max-width: 900px;
    display: flex;
    justify-content: center;
    margin-bottom: 48px;
  }

  .create-drafts-divider {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--color-border);
    z-index: 0;
  }

  .create-drafts-btn {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 999px;
    border: 1px solid var(--color-border);
    background: var(--color-surface);
    color: var(--color-text-tertiary);
    font-size: 14px;
    font-weight: 500;
    font-family: "Inter", sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

    &:hover:not(.disabled) {
      border-color: var(--color-text-tertiary);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &.disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .create-drafts-icon {
    color: var(--color-text);
    flex-shrink: 0;
  }

  .create-drafts-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--color-primary);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }

  .create-footer-quote {
    text-align: center;
    font-style: italic;
    font-size: 18px;
    font-family: "Libre Baskerville", serif;
    color: var(--color-text-tertiary);
    max-width: 800px;
    position: relative;
    padding: 10px 20px;
    background-color: var(--color-background);
    border-radius: 4px;

    &::after {
      content: "";
      display: block;
      width: 50px;
      height: 1px;
      background: var(--color-border);
      margin: 15px auto 0;
    }
  }

  .create-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .create-confirm-backdrop {
    z-index: 1001;
  }

  .create-modal {
    background: var(--color-surface);
    border-radius: 8px;
    padding: 24px 28px;
    max-width: 480px;
    width: 90%;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    position: relative;
  }

  .create-drafts-modal {
    max-width: 520px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
  }

  .create-confirm-modal {
    max-width: 400px;
  }

  .create-modal-title {
    margin: 0 0 12px;
    font-size: 22px;
    font-family: "Libre Baskerville", serif;
    color: var(--color-text-tertiary);
  }

  .create-modal-text {
    font-size: 14px;
    color: var(--color-text);
    margin-bottom: 18px;
  }

  .create-modal-actions {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    flex-wrap: wrap;
  }

  .create-modal-btn {
    flex: 1;
    padding: 10px 16px;
    border-radius: 999px;
    border: 1px solid var(--color-border);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    min-width: 100px;
  }

  .create-modal-btn.secondary {
    background: transparent;
    color: var(--color-text-tertiary);
  }

  .create-modal-btn.danger {
    background: #c0392b;
    color: #fff;
    border-color: #c0392b;
  }

  .create-modal-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .create-modal-close {
    position: absolute;
    top: 8px;
    right: 10px;
    border: none;
    background: transparent;
    font-size: 20px;
    cursor: pointer;
    color: var(--color-text);
    line-height: 1;
  }

  .create-drafts-list {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    max-height: 50vh;
  }

  .create-drafts-list li {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid var(--color-border);

    &:last-child {
      border-bottom: none;
    }
  }

  .create-draft-row {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 12px 14px 0;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-family: "Inter", sans-serif;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.03);
    }
  }

  .create-draft-row-main {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    width: 100%;
  }

  .create-draft-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-text-tertiary);
  }

  .create-draft-type {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 2px 8px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.06);
    color: var(--color-text);
  }

  .create-draft-type--story {
    background: rgba(46, 90, 75, 0.12);
    color: var(--color-primary);
  }

  .create-draft-type--poem {
    background: rgba(90, 70, 120, 0.12);
    color: #5a4678;
  }

  .create-draft-updated {
    font-size: 12px;
    color: var(--color-text);
    opacity: 0.75;
  }

  .create-draft-delete {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    border: none;
    background: transparent;
    color: var(--color-text);
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      opacity: 1;
      color: #c0392b;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }
  }

  @media (max-width: 768px) {
    .create-main-heading {
      font-size: 32px;
    }

    .create-cards-wrapper {
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }

    .create-card {
      width: 100%;
      max-width: 350px;
    }
  }
`;
